import { BrandAssetHash } from "@ascleo/frontdesk-contracts/branding/model";
import { BrandingAssetResponse } from "@ascleo/frontdesk-contracts/branding/public";
import {
  CachedBrandingAsset,
  CachedBrandingRecord,
  type BrandingCache,
  type CachedBrandingAssetType,
} from "@ascleo/frontdesk-shared/branding";
import { Schema } from "effect";

/** The web cache returns assets already decoded at its storage boundary. */
interface WebBrandingCache extends BrandingCache {
  readAsset(hash: Parameters<BrandingCache["readAsset"]>[0]): Promise<CachedBrandingAssetType | undefined>;
}

const ActivePointer = Schema.Struct({
  cacheFormatVersion: Schema.Literal(1),
  publicationVersion: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0))),
});
const decodeAuthority = Schema.decodeUnknownSync(BrandAssetHash);
const decodeHash = Schema.decodeUnknownSync(BrandAssetHash);
const decodeVersion = Schema.decodeUnknownSync(
  Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0))),
);
const decodePointer = Schema.decodeUnknownSync(ActivePointer, {
  onExcessProperty: "error",
});
const decodeAsset = Schema.decodeUnknownSync(CachedBrandingAsset, {
  onExcessProperty: "error",
});
const decodeRecord = Schema.decodeUnknownSync(CachedBrandingRecord, {
  onExcessProperty: "error",
});
const decodeMetadata = Schema.decodeUnknownSync(BrandingAssetResponse, {
  onExcessProperty: "error",
});

const DB_VERSION = 2;
const RECORD_STORE = "records";
const META_STORE = "meta";
const ASSET_STORE = "assets";
const ACTIVE_KEY = "active";

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", Uint8Array.from(bytes));
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
}

function databaseName(authority: string): string {
  return `frontdesk-branding-v1:${authority}`;
}

function assetCacheName(authority: string): string {
  return `frontdesk-branding-assets-v1:${authority}`;
}

function assetRequest(authority: string, hash: string): Request {
  const origin = globalThis.location?.origin ?? "https://frontdesk.invalid";
  return new Request(
    new URL(`/__frontdesk-branding-cache/v1/${authority}/assets/${hash}`, origin),
  );
}

async function putCacheStorageAsset(
  authority: string,
  asset: CachedBrandingAssetType,
): Promise<void> {
  const cache = await caches.open(assetCacheName(authority));
  await cache.put(
    assetRequest(authority, asset.metadata.assetHash),
    new Response(Uint8Array.from(asset.bytes), {
      headers: {
        "content-type": asset.metadata.mediaType,
        "content-length": String(asset.bytes.byteLength),
        "x-branding-asset-hash": asset.metadata.assetHash,
      },
    }),
  );
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.addEventListener("success", () => resolve(request.result), { once: true });
    request.addEventListener(
      "error",
      () => reject(request.error ?? new Error("IndexedDB request failed.")),
      { once: true },
    );
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.addEventListener("complete", () => resolve(), { once: true });
    transaction.addEventListener(
      "error",
      () => reject(transaction.error ?? new Error("IndexedDB transaction failed.")),
      { once: true },
    );
    transaction.addEventListener(
      "abort",
      () => reject(transaction.error ?? new Error("IndexedDB transaction aborted.")),
      { once: true },
    );
  });
}

function openDatabase(authority: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(databaseName(authority), DB_VERSION);
    request.addEventListener("upgradeneeded", () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(RECORD_STORE)) {
        database.createObjectStore(RECORD_STORE);
      }
      if (!database.objectStoreNames.contains(META_STORE)) {
        database.createObjectStore(META_STORE);
      }
      if (!database.objectStoreNames.contains(ASSET_STORE)) {
        database.createObjectStore(ASSET_STORE);
      }
    });
    request.addEventListener("success", () => resolve(request.result), { once: true });
    request.addEventListener(
      "error",
      () => reject(request.error ?? new Error("Branding IndexedDB open failed.")),
      { once: true },
    );
  });
}

async function readStoredAsset(
  authority: string,
  hashInput: unknown,
): Promise<CachedBrandingAssetType | undefined> {
  const hash = decodeHash(hashInput);
  const cache = await caches.open(assetCacheName(authority));
  const response = await cache.match(assetRequest(authority, hash));
  if (response !== undefined) {
    const bytes = new Uint8Array(await response.arrayBuffer());
    const metadata = decodeMetadata({
      assetHash: response.headers.get("x-branding-asset-hash"),
      mediaType: response.headers.get("content-type"),
      byteLength: Number(response.headers.get("content-length")),
    });
    return { metadata, bytes };
  }

  const database = await openDatabase(authority);
  try {
    const raw: unknown = await requestResult(
      database.transaction(ASSET_STORE, "readonly").objectStore(ASSET_STORE).get(hash),
    );
    if (raw === undefined) return undefined;
    const asset = decodeAsset(raw);
    if (asset.metadata.assetHash !== hash) {
      throw new Error("IndexedDB branding asset key is inconsistent.");
    }
    // Cache Storage can be evicted independently on WebKit. The verified IndexedDB mirror keeps
    // offline boot durable; rehydration is best-effort because the runtime validates bytes again.
    await putCacheStorageAsset(authority, asset).catch(() => undefined);
    return asset;
  } finally {
    database.close();
  }
}

/**
 * Create the hosted-web cache using Cache Storage objects and one IndexedDB active pointer.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const cache = makeWebBrandingCache(authority);
 * ```
 */
export function makeWebBrandingCache(authorityInput: unknown): WebBrandingCache {
  const authority = decodeAuthority(authorityInput);

  const readActive: BrandingCache["readActive"] = async () => {
    const database = await openDatabase(authority);
    try {
      const pointerTransaction = database.transaction(META_STORE, "readonly");
      const pointerRaw: unknown = await requestResult(
        pointerTransaction.objectStore(META_STORE).get(ACTIVE_KEY),
      );
      if (pointerRaw === undefined) return undefined;
      const pointer = decodePointer(pointerRaw);
      // WebKit may auto-close an IndexedDB transaction after the awaited pointer request. The
      // version record is immutable, so reading it in a fresh transaction preserves the atomic
      // pointer contract without relying on browser-specific transaction liveness.
      const recordTransaction = database.transaction(RECORD_STORE, "readonly");
      const recordRaw: unknown = await requestResult(
        recordTransaction.objectStore(RECORD_STORE).get(pointer.publicationVersion),
      );
      if (recordRaw === undefined) return undefined;
      const record = decodeRecord(recordRaw);
      return record.manifest.publicationVersion === pointer.publicationVersion
        ? recordRaw
        : undefined;
    } finally {
      database.close();
    }
  };

  return {
    readActive,
    readAsset: (hash) => readStoredAsset(authority, hash),
    stageAsset: async (asset) => {
      const hash = decodeHash(asset.metadata.assetHash);
      if (asset.bytes.byteLength !== asset.metadata.byteLength) {
        throw new Error("Web branding asset length does not match its metadata.");
      }
      if ((await sha256Hex(asset.bytes)) !== hash) {
        throw new Error("Web branding asset hash verification failed.");
      }
      await putCacheStorageAsset(authority, asset);
      const database = await openDatabase(authority);
      try {
        const transaction = database.transaction(ASSET_STORE, "readwrite");
        const done = transactionDone(transaction);
        transaction.objectStore(ASSET_STORE).put(asset, hash);
        await done;
      } finally {
        database.close();
      }
    },
    stageRecord: async (record) => {
      const encoded = Schema.encodeUnknownSync(CachedBrandingRecord)(record);
      const database = await openDatabase(authority);
      try {
        const transaction = database.transaction(RECORD_STORE, "readwrite");
        const done = transactionDone(transaction);
        transaction
          .objectStore(RECORD_STORE)
          .put(encoded, record.manifest.publicationVersion);
        await done;
      } finally {
        database.close();
      }
    },
    commitActive: async (versionInput) => {
      const version = decodeVersion(versionInput);
      const database = await openDatabase(authority);
      try {
        const readTransaction = database.transaction(RECORD_STORE, "readonly");
        const recordRaw: unknown = await requestResult(
          readTransaction.objectStore(RECORD_STORE).get(version),
        );
        if (recordRaw === undefined) {
          throw new Error("Web branding candidate record is missing.");
        }
        const record = decodeRecord(recordRaw);
        if (record.manifest.publicationVersion !== version) {
          throw new Error("Web branding candidate version is inconsistent.");
        }
        for (const hash of record.referencedHashes) {
          if ((await readStoredAsset(authority, hash)) === undefined) {
            throw new Error("Web branding candidate assets are incomplete.");
          }
        }
        const commit = database.transaction(META_STORE, "readwrite");
        const done = transactionDone(commit);
        commit.objectStore(META_STORE).put(
          { cacheFormatVersion: 1, publicationVersion: version },
          ACTIVE_KEY,
        );
        await done;
      } finally {
        database.close();
      }
    },
    abandon: async (versionInput) => {
      const version = decodeVersion(versionInput);
      const rawActive = await readActive();
      const active = rawActive === undefined ? undefined : decodeRecord(rawActive);
      if (active?.manifest.publicationVersion === version) return;
      const database = await openDatabase(authority);
      try {
        const transaction = database.transaction(RECORD_STORE, "readwrite");
        const done = transactionDone(transaction);
        transaction.objectStore(RECORD_STORE).delete(version);
        await done;
      } finally {
        database.close();
      }
    },
    cleanup: async (referencedHashes) => {
      const retained = new Set<string>(referencedHashes);
      const rawActive = await readActive();
      const active = rawActive === undefined ? undefined : decodeRecord(rawActive);
      for (const hash of active?.referencedHashes ?? []) retained.add(hash);
      const cache = await caches.open(assetCacheName(authority));
      const requests = await cache.keys();
      const stale = requests
        .map((request) => request.url.split("/").at(-1))
        .filter((hash): hash is string =>
          hash !== undefined && /^[0-9a-f]{64}$/.test(hash) && !retained.has(hash),
        )
        .slice(0, 128);
      await Promise.all(
        stale.map((hash) => cache.delete(assetRequest(authority, hash))),
      );
      const database = await openDatabase(authority);
      try {
        const read = database.transaction(ASSET_STORE, "readonly");
        const keys = await requestResult(read.objectStore(ASSET_STORE).getAllKeys());
        const staleMirrors = keys
          .filter(
            (key): key is string =>
              typeof key === "string"
              && /^[0-9a-f]{64}$/.test(key)
              && !retained.has(key),
          )
          .slice(0, 128);
        if (staleMirrors.length > 0) {
          const remove = database.transaction(ASSET_STORE, "readwrite");
          const done = transactionDone(remove);
          for (const hash of staleMirrors) remove.objectStore(ASSET_STORE).delete(hash);
          await done;
        }
      } finally {
        database.close();
      }
    },
  };
}
